const name = 'Convert Playlist';

const url = 'https://convertplaylist.net';

export const TABS = ['single', 'account', 'profile'];

export const metaDefaults = {
  // Best practice in SSR: absolute URLs for OG/Twitter images
  ogImage: `${url}/og-image.png`,
  keywords:
    'playlist converter, music transfer, spotify to apple music, youtube music converter',
};

export const homeMeta = {
  url,
  title: name,
  description: `${name}. Easily transfer your playlists across various music platforms.`,
};

export const convertMeta = {
  url: `${url}/convert`,
  title: `Transfer Music Playlists | ${name}`,
  description: `Transfer playlists between music platforms in seconds with ${name}.`,
};

export const supportMeta = {
  url: `${url}/support`,
  title: `Support | ${name}`,
  description: `Support ${name} to help cover hosting, API costs, and ongoing development.`,
};

export const cookiePolicyMeta = {
  url: `${url}/cookie-policy`,
  title: `Cookie Policy | ${name}`,
  description: `Cookie Policy for ${name}. Learn how cookies are used and how to manage your preferences.`,
};

export const privacyPolicyMeta = {
  url: `${url}/privacy-policy`,
  title: `Privacy Policy | ${name}`,
  description: `Privacy Policy for ${name}. Learn what data we collect, how it’s used, and your choices.`,
};

export const termsOfServiceMeta = {
  url: `${url}/terms-of-service`,
  title: `Terms of Service | ${name}`,
  description: `Terms of Service for ${name}. Review usage rules, limitations, and responsibilities.`,
};

export const retryMeta = {
  url: `${url}/retry`,
  title: `Retry Conversion | ${name}`,
  description: `Retry a failed playlist conversion on ${name}.`,
};

export const historyMeta = {
  url: `${url}/history`,
  title: `Conversion History | ${name}`,
  description: `View your past playlist conversions on ${name}.`,
};

const oauthCallbackMeta = {
  url,
  title: `Connecting | ${name}`,
  description: `Connecting your account to ${name}.`,
};

export const notFoundMeta = {
  url,
  title: `404 | ${name}`,
  description: `The page you are looking for could not be found.`,
};

const staticMetaMap = {
  '/': homeMeta,
  '/history': historyMeta,
  '/support': supportMeta,
  '/cookie-policy': cookiePolicyMeta,
  '/privacy-policy': privacyPolicyMeta,
  '/terms-of-service': termsOfServiceMeta,
};

// --- Converter routing/meta (dynamic) ---
const PLATFORM_CANONICAL = {
  apple: 'apple-music',
  'apple-music': 'apple-music',

  youtube: 'youtube-music',
  'youtube-music': 'youtube-music',

  spotify: 'spotify',
};

const PLATFORM_TITLES = {
  'apple-music': 'Apple Music',
  'youtube-music': 'YouTube Music',
  spotify: 'Spotify',
};

const VALID_PLATFORMS = new Set(Object.values(PLATFORM_CANONICAL));

const normalizePlatform = (platform) => {
  const _platform = String(platform || '')
    .toLowerCase()
    .trim();

  return PLATFORM_CANONICAL[_platform] || _platform;
};

const titleFor = (platform) => PLATFORM_TITLES[platform] || platform;

const parseConversionPath = (path) => {
  // Matches /convert/source-to-target OR /convert/source-to-target/tab
  // Captures the pair segment specifically
  const match = String(path || '').match(
    /^\/convert\/([a-z0-9-]+-to-[a-z0-9-]+)(?:\/([a-z0-9-]+))?$/
  );

  if (!match) return null;

  const pairPart = match[1];
  const tabPart = match[2];

  // If a tab is present in the URL, validate it
  if (tabPart && !TABS.includes(tabPart)) return null;

  const pairMatch = pairPart.match(/^([a-z0-9-]+)-to-([a-z0-9-]+)$/);

  if (!pairMatch) return null;

  const source = normalizePlatform(pairMatch[1]);
  const target = normalizePlatform(pairMatch[2]);

  if (!VALID_PLATFORMS.has(source) || !VALID_PLATFORMS.has(target)) return null;

  if (source === target) return null;

  return { source, target };
};

const buildConversionMeta = ({ source, target }) => {
  const sourceName = titleFor(normalizePlatform(source));
  const targetName = titleFor(normalizePlatform(target));

  // Canonical is the served 200 URL; the bare pair 301-redirects to /single.
  const canonicalPath = `/convert/${source}-to-${target}/single`;
  const canonicalUrl = `${url}${canonicalPath}`;

  return {
    canonicalUrl,
    canonicalPath,
    url: canonicalUrl, // keep meta.url canonical for consistency
    title: `${sourceName} to ${targetName} | ${name}`,
    description: `Transfer ${sourceName} playlists to ${targetName} in seconds with ${name}.`,
  };
};

const trimTrailingSlash = (path) => {
  const _path = String(path || '');

  if (!_path) return '/';

  const parsedPath = _path.replace(/\/+$/, '');

  return parsedPath || '/';
};

const escapeHtml = (value) => {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
};

// For client usage
export function getConversionMeta({ source, target }) {
  return buildConversionMeta({ source, target });
}

// --- Sitemap ---

// All indexable URLs: home, the converter, every canonical pair page, and the
// static legal/support pages. User-scoped routes (history, retry, oauth) are
// intentionally excluded — see robots.txt.
export function buildSitemapUrls() {
  const platforms = [...VALID_PLATFORMS];

  const pairs = platforms.flatMap((source) => {
    return platforms
      .filter((target) => target !== source)
      .map((target) => ({
        loc: buildConversionMeta({ source, target }).canonicalUrl,
        priority: '0.8',
      }));
  });

  return [
    { loc: url, priority: '1.0' },
    { loc: `${url}/convert/single`, priority: '0.9' },
    ...pairs,
    { loc: `${url}/support`, priority: '0.3' },
    { loc: `${url}/privacy-policy`, priority: '0.2' },
    { loc: `${url}/terms-of-service`, priority: '0.2' },
    { loc: `${url}/cookie-policy`, priority: '0.2' },
  ];
}

export function renderSitemap() {
  const entries = buildSitemapUrls()
    .map(
      ({ loc, priority }) =>
        `  <url>\n    <loc>${escapeHtml(loc)}</loc>\n    <priority>${priority}</priority>\n  </url>`
    )
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${entries}\n</urlset>\n`;
}

// Exposed so the server/SSR handler can 301 alias -> canonical easily
export function getCanonicalRedirect(path) {
  const parsedPath = trimTrailingSlash(path);

  // 1. Auto-redirect bare /convert to /convert/single
  if (parsedPath === '/convert') return '/convert/single';

  // 2. Capture the pair and optional tab
  const match = String(parsedPath || '').match(
    /^\/convert\/([a-z0-9-]+-to-[a-z0-9-]+)(?:\/([a-z0-9-]+))?$/
  );

  if (match) {
    const pairPart = match[1];
    const tabPart = match[2];

    // If they provided the pair but NO tab, redirect to /single
    if (!tabPart) {
      return `/convert/${pairPart}/single`;
    }
  }

  return null;
}

// Main renderer used by SSR to inject head tags
export function renderMeta(path) {
  const parsedPath = trimTrailingSlash(path);

  // Handle OAuth callback routes
  if (parsedPath.startsWith('/oauth/callback/')) {
    return formatResponse(oauthCallbackMeta, 200);
  }

  // Handle retry routes
  if (parsedPath.startsWith('/retry/')) {
    return formatResponse(retryMeta, 200);
  }

  // Handle custom /convert routes
  const isCustom =
    parsedPath === '/convert' ||
    TABS.some((tab) => parsedPath === `/convert/${tab}`);

  if (isCustom) {
    return formatResponse(convertMeta, 200, buildAppJsonLd());
  }

  // Static routes
  let status = 200;

  let meta = staticMetaMap[parsedPath];

  let jsonLd = parsedPath === '/' ? buildAppJsonLd() : null;

  // Dynamic pair routes
  if (!meta) {
    const parsed = parseConversionPath(parsedPath);

    if (parsed) {
      meta = buildConversionMeta(parsed);
      jsonLd = buildPairJsonLd(parsed, meta.canonicalUrl);
    }
  }

  // 404
  if (!meta) {
    status = 404;
    jsonLd = null;

    meta = {
      ...notFoundMeta,
      canonicalUrl: url,
      // reflect the actual missing path (useful for debugging/logs)
      url: `${url}${parsedPath}`,
    };
  }

  return formatResponse(meta, status, jsonLd);
}

// --- Structured data (JSON-LD) ---

// Serialize for safe inline embedding inside a <script> tag.
const serializeJsonLd = (data) => {
  return JSON.stringify(data).replaceAll('<', '\\u003c');
};

function buildAppJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    url,
    name,
    operatingSystem: 'Web',
    description: homeMeta.description,
    applicationCategory: 'MultimediaApplication',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  };
}

function buildPairJsonLd({ source, target }, canonicalUrl) {
  const sourceName = titleFor(normalizePlatform(source));
  const targetName = titleFor(normalizePlatform(target));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: url },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Convert',
        item: `${url}/convert/single`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${sourceName} to ${targetName}`,
        item: canonicalUrl,
      },
    ],
  };
}

function formatResponse(meta, status, jsonLd = null) {
  const is404 = status === 404;

  const { title, description } = meta;

  const canonicalUrl = meta.canonicalUrl || meta.url;

  const head = `
    <title>${escapeHtml(title)}</title>
    <meta name="description"         content="${escapeHtml(description)}" />
    <meta name="keywords"            content="${escapeHtml(metaDefaults.keywords)}" />
    <link rel="canonical"            href="${escapeHtml(canonicalUrl)}" />
    
    ${is404 ? `<meta name="robots"   content="noindex, nofollow" />` : ''}
    
    <meta property="og:type"         content="website" />
    <meta property="og:url"          content="${escapeHtml(canonicalUrl)}" />
    <meta property="og:title"        content="${escapeHtml(title)}" />
    <meta property="og:description"  content="${escapeHtml(description)}" />
    <meta property="og:image"        content="${escapeHtml(metaDefaults.ogImage)}" />
    
    <meta name="twitter:card"        content="summary_large_image" />
    <meta name="twitter:url"         content="${escapeHtml(canonicalUrl)}" />
    <meta name="twitter:title"       content="${escapeHtml(title)}" />
    <meta name="twitter:description" content="${escapeHtml(description)}" />
    <meta name="twitter:image"       content="${escapeHtml(metaDefaults.ogImage)}" />
    ${jsonLd ? `<script type="application/ld+json">${serializeJsonLd(jsonLd)}</script>` : ''}
  `;

  return { head, status, canonicalUrl };
}
